import { Component, ViewChild, ElementRef, OnInit, OnDestroy, AfterViewInit, Input } from '@angular/core';
import { LangService } from '../../core/lang.service';
import { ElementType,IContentElementIframe } from '../models';
enum Func {
  CLEAR,
  NEG,
  PERC,
  PI,
}
enum Operator {
  SQRT,
  PERC,
  DIVIDE,
  MULTIPLY,
  MINUS,
  PLUS,
  EQUAL,
}
enum ButtonType {
  FUNCTION,
  VALUE,
  OPERATOR
}
const COMPUTER_DEC_DELIM = '.';
interface ICalcButton {
  type: ButtonType;
  operator?: Operator;
  func?: Func;
  num?: string | number;
  caption?: string;
  isDoubleWidth?: boolean;
  isTripleWidth?: boolean;
}
interface ICalculatorModel {
    display: string;
    storedValue: number;
    newValue: string;
    newOperator: Operator;
    lastNewValue: string;
    lastNewOperator: Operator;
}


@Component({
  selector: 'widget-calculator',
  templateUrl: './widget-calculator.component.html',
  styleUrls: ['./widget-calculator.component.scss']
})
export class WidgetCalculatorComponent implements OnInit, OnDestroy, AfterViewInit  {
  
  @Input() zoom:number;
  @Input() isSimple:boolean;
  @ViewChild('container') containerEl: ElementRef<HTMLInputElement>;
  focused = false;
  
  constructor(
    public lang: LangService,
  ) { }



  model: ICalculatorModel = {
    display: '0',
    storedValue: 0,
    newValue: '',
    newOperator: null,
    lastNewValue: '',
    lastNewOperator: null,
  };
  iframeUrl 
  element:IContentElementIframe = {
     elementType:ElementType.IFRAME,
     url:''
  }
  DECIMAL_DELIM = this.lang.tra('decimal_delim');

  functionButtons: ICalcButton[] = [
    {type: ButtonType.OPERATOR, func: Func.CLEAR, caption: 'C', isTripleWidth: true, },
    {type: ButtonType.OPERATOR, func: Func.NEG, caption: '±', },
    {type: ButtonType.OPERATOR, func: Func.PERC, caption: '%', },
    {type: ButtonType.OPERATOR, func: Func.PI, caption: 'π', },
  ];
  numberButtons: ICalcButton[] = [
    {type: ButtonType.VALUE, num: 7},
    {type: ButtonType.VALUE, num: 8},
    {type: ButtonType.VALUE, num: 9},
    {type: ButtonType.VALUE, num: 4},
    {type: ButtonType.VALUE, num: 5},
    {type: ButtonType.VALUE, num: 6},
    {type: ButtonType.VALUE, num: 1},
    {type: ButtonType.VALUE, num: 2},
    {type: ButtonType.VALUE, num: 3},
    {type: ButtonType.VALUE, num: 0, isDoubleWidth: true, },
    {type: ButtonType.VALUE, num: COMPUTER_DEC_DELIM, caption: this.DECIMAL_DELIM },
  ];
  operatorButtons: ICalcButton[] = [
    {type: ButtonType.OPERATOR, operator: Operator.SQRT, caption: '√'},
    {type: ButtonType.OPERATOR, operator: Operator.DIVIDE, caption: '÷'},
    {type: ButtonType.OPERATOR, operator: Operator.MULTIPLY, caption: '×'},
    {type: ButtonType.OPERATOR, operator: Operator.MINUS, caption: '-'},
    {type: ButtonType.OPERATOR, operator: Operator.PLUS, caption: '+'},
    {type: ButtonType.OPERATOR, operator: Operator.EQUAL, caption: '='},
  ];

  ngOnInit() {
    window.addEventListener('keydown', this.onKeyboardDown);
    this.getIframeURL()
  }

  ngOnDestroy() {
    window.removeEventListener('keydown', this.onKeyboardDown);
  }

  ngAfterViewInit() {
    this.containerEl.nativeElement.focus();
  }

  onKeyboardDown = (event: KeyboardEvent) => {
    // console.log('event', event);
    if (!this.focused) return;
    
    switch (event.key) {
      case '0':
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
        return this.pressNumber(event.key);
      case '.':
      case ',':
        return this.pressNumber(COMPUTER_DEC_DELIM);
      case 'p':
        return this.pressFunction(Func.PI);
      case '=':
        return this.pressOperator(Operator.EQUAL);
      case 'Enter':
        return this.pressOperator(Operator.EQUAL);
      case '+':
        return this.pressOperator(Operator.PLUS);
      case '-':
        return this.pressOperator(Operator.MINUS);
      case '*':
      case 'x':
        return this.pressOperator(Operator.MULTIPLY);
      case '/':
      case 'd':
        return this.pressOperator(Operator.DIVIDE);
      case 's':
        return this.pressOperator(Operator.SQRT);
      case '%':
        return this.pressOperator(Operator.PERC);
      case 'c':
        return this.pressFunction(Func.CLEAR);
      case 'm':
        return this.pressFunction(Func.NEG);
      case 'Delete':
        return this.pressFunction(Func.CLEAR);
      case 'Backspace':
        return this.applyBackspace();
    }
  }

  getScreenDisplay() {
    return this.model.newValue || this.model.display;
  }
  getFontDisplayScale() {
    const len = this.getScreenDisplay().length;
    const softCap = 8;
    const baseScale = 1;
    const maxScale = 2;
    if (len > softCap) {
      return baseScale;
    } else {
      return baseScale + (maxScale - baseScale) * (1 - len / softCap);
    }
  }

  pressFunction(func: Func) {
    switch (func) {
      case Func.PERC:
        this.processStoredOperation(true);
        this.processOperation(Operator.PERC, '' ); break;
      case Func.NEG:
        this.invertActiveSign(); break;
      case Func.PI:
        this.model.newValue = this.renderDisplayValue(Math.PI); break;
      case Func.CLEAR:
        this.clearDisplay(); break;
    }
  }

  pressNumber(num: string | number) {
    const currentValue = this.model.newValue;
    if (currentValue.length < 9) {
      if (num === COMPUTER_DEC_DELIM) {
        if (currentValue.indexOf(this.DECIMAL_DELIM) === -1) {
          this.model.newValue += this.DECIMAL_DELIM;
        }
      } else {
        this.model.newValue += num;
      }
    }
  }

  pressOperator(operator: Operator) {
    switch (operator) {
      case Operator.EQUAL:
        this.processStoredOperation(); break;
      case Operator.DIVIDE:
      case Operator.MULTIPLY:
      case Operator.MINUS:
      case Operator.PLUS:
        this.storeOperation(operator); break;
      case Operator.SQRT:
        this.processStoredOperation(true);
        this.processOperation(Operator.SQRT, '' ); break;
    }
  }

  applyBackspace() {
    if (this.model.newValue) {
      this.model.newValue = this.model.newValue.slice(0, this.model.newValue.length - 1);
    } else {
      this.model.display = this.model.display.slice(0, this.model.display.length - 1);
      this.model.storedValue = parseInt(this.model.storedValue.toString().slice(0, this.model.storedValue.toString().length - 1), 10)
    }
  }

  clearDisplay() {
    if (this.model.newValue) {
      this.model.newValue = '';
      this.model.display = '0';
      this.model.storedValue = 0;
      this.model.newOperator = null;
    } else if (this.model.newOperator) {
      this.model.newOperator = null;
      this.model.display = '0';
      this.model.storedValue = 0;
    } else {
      this.model.display = '0';
      this.model.storedValue = 0;
      this.model.lastNewOperator = null;
      this.model.lastNewValue = '';
    }
  }

  processStoredOperation(isBlockReapply: boolean= false) {
    // console.log('processStoredOperation', this.model.newOperator, this.model.lastNewOperator, this.model.newValue)
    if (this.model.newOperator) {
      this.processOperation(this.model.newOperator, this.model.newValue || this.model.display);
    } else if (this.model.lastNewOperator && !isBlockReapply) {
      this.model.newValue;
      this.processOperation(this.model.lastNewOperator, this.model.lastNewValue);
    } else if (this.model.newValue) {
      this.model.display = this.model.newValue;
      this.captureDisplayValue();
      this.model.newValue = '';
      this.model.lastNewOperator = null;
      this.model.lastNewValue = undefined;
    }
  }

  invertActiveSign() {
    if (this.model.newValue) {
      this.model.newValue = this.invertValueSign(this.model.newValue);
    } else {
      this.model.display = this.invertValueSign(this.model.display);
      this.model.storedValue = +this.model.display;
    }
  }

  invertValueSign(displayValue: string) {
    const newValueNum = this.parseDisplayValue(displayValue);
    return this.renderDisplayValue(-1 * newValueNum);
  }

  processOperation(op: Operator, newValue: string) {
    const baseValue = this.model.storedValue;
    if (this.model.newValue) {
      newValue = this.model.newValue;
    } else {
      newValue = this.model.display;
    }
    const newValueNum = this.parseDisplayValue(newValue);
    let result: number;
    switch (op) {
      case Operator.PERC     : result = baseValue / 100; break;
      case Operator.SQRT     : result = Math.sqrt(baseValue); break;
      case Operator.DIVIDE   : result = baseValue / newValueNum; break;
      case Operator.MULTIPLY : result = baseValue * newValueNum; break;
      case Operator.MINUS    : result = baseValue - newValueNum; break;
      case Operator.PLUS     : result = baseValue + newValueNum; break;
    }
    // console.log('processOperation', result, op, baseValue, newValueNum)
    this.model.display = this.renderDisplayValue(result);
    this.captureDisplayValue();
    this.model.newValue = '';
    this.model.newOperator = null;
    this.model.lastNewOperator = op;
    this.model.lastNewValue = newValue;
  }

  captureDisplayValue() {
    this.model.storedValue = this.parseDisplayValue(this.model.display);
  }

  parseDisplayValue(str: string) {
    const computerDecimal = str.split(this.DECIMAL_DELIM).join('.');
    return parseFloat(computerDecimal);
  }
  renderDisplayValue(num: number) {
    let numTruncated = parseFloat((num).toFixed(9));
    const displayDecimal = ('' + numTruncated).split('.').join(this.DECIMAL_DELIM);
    return displayDecimal;
  }

  storeOperation(operator: Operator) {
    if (this.model.newOperator && this.model.newValue) {
      this.processStoredOperation();
    } else if (this.model.newValue) {
      this.model.display = this.model.newValue;
      this.model.storedValue = this.parseDisplayValue(this.model.display);
      this.model.newValue = '';
    }
    this.model.newOperator = operator;
  }
  getIframeURL(){
    let url = window.location.protocol + "//" + window.location.host + "/assets/sci_calc/index.html"
    this.element.url = url;
  
  }
  
  onCalculatorFocus() {
    this.focused = true;
  }

  onCalculatorFocusOut() {
    this.focused = false;
  }
}
